/**
 * The margin rule (docs/internal/architecture/model-registry-and-admin-plan.md
 * section 7, decisions 17.4 and 17.6; 2026-10-06), as one pure function the
 * three places that must agree all call: lane B's route resolver on every
 * call and lane C's admin API on every write that could change it (lane D's
 * Models tab renders the API's verdicts as its four margin dots; see THE
 * MULTIPLIER for why it does not recompute them). Nothing here reads the
 * database, the settings, Redis or the environment beyond what
 * `retailModelPricing` reads (the retail multiplier), so the module is
 * isomorphic and safe to import from a `'use client'` component.
 *
 * THE RULE. Per leg, a route's cost against the model's retail
 * (`retailForModel(list_price, cache_billing)`, below):
 *   - input and output: the cost must be STRICTLY under retail, else `under`
 *     and the route is dark (`margin`). A maker price rise that pushes its
 *     own direct cost above the retail of a stale list price darkens the HOME
 *     route, which is the right failure: the model becomes unservable, the
 *     alert fires and the admin raises the list price.
 *   - longContext: a tiered route's tier legs against the retail's tier legs
 *     (or the flat retail when the model has no tier), and ALSO against the
 *     flat retail when the route's threshold is lower than the retail's (the
 *     band in between bills the user flat). A flat route under a tiered
 *     retail is `n/a` (cheaper on long prompts, Grok 4.3 on Bedrock).
 *   - cacheRead and cacheWrite: the route's leg (its stated rate, or its
 *     INPUT rate when it has none, which is what `computeProxyCostNanocredits`
 *     bills) against the retail's cache leg. This is where four of the six
 *     Fireworks rows routed to Bedrock lose money today (Kimi K2.5, GLM 5,
 *     MiniMax M2.5, gpt-oss-120b: a retail cache read far below Bedrock's
 *     input rate). A cache-leg violation darkens the route UNLESS the model's
 *     `cache_billing` is `input` (the user pays the retail input rate for
 *     cached tokens on every route, so the price stays route-independent and
 *     the leg holds) or the route's `reports_cached_tokens` is `false` (the
 *     probe showed the leg cannot occur there; `n/a`). A null (never probed)
 *     counts as "may report" (decision 17.4), so those four routes are dark
 *     on a registry chain until probed or the admin picks `input`.
 *
 * ONE RETAIL, TWO READERS. `retailForModel` is the retail the user is
 * actually billed for a row (lib/models/catalog.ts `pricingWithCacheAtInput`
 * under `input` billing, then the retail multiplier), and it is the same
 * computation `resolveRetailModelPricing` applies on every charge path. The
 * first draft read `cache_billing` here only, so picking `input` opened the
 * four routes above while every settle still billed the retail cache
 * discount (review, 2026-10-06); the margin verdict and the bill must come
 * from one function or they drift.
 *
 * THE MULTIPLIER. Retail is list x `MODEL_RETAIL_MULTIPLIER` (default 1.4,
 * lib/economics/constants.ts). The rule is strictly under, so at a
 * multiplier of 1 or below every parity home route is `under` and, with the
 * registry on, every model is unservable ("No API key available"): a
 * below-list promotion is a full outage on the registry chain, not a
 * discount. The multiplier is a server environment variable, so a browser
 * render always assumes the default: the Models tab takes its verdicts from
 * the admin API, never recomputes them client side.
 *
 * Fail-closed on price: a route whose cost is null or whose `cost_checked_at`
 * is null is `cost_unconfirmed` and dark, and every leg reads `unknown`. A
 * model with no confirmed list price has no retail to compare against:
 * `unknown` legs, and `margin` as the blocker, because an unprovable margin
 * is no margin. Staleness (a cost checked more than 90 days ago) is a badge
 * in the tab, never a gate (decision 17.6): `isStaleCost` is exported for
 * the badge and `routeBlockers` ignores it.
 *
 * THE GRANT-FUNDED EXCEPTION (owner, 2026-10-06: "we route everything via
 * bedrock first, right? we should, even if they are slightly more expensive,
 * we'd burn credits (confirmed by aws)"; docs/adr/0056-routes-paid-from-a-
 * provider-credit-grant-may-serve-above-retail.md). AWS's credit grant pays
 * Bedrock's bill, so a route whose SERVING provider is named in the platform
 * setting `models.grantFunded` may serve although a leg is `under`, provided
 * input and output are known (`unknown` still fails closed: a route with no
 * provable price never serves) and the worst leg is within the setting's
 * ceiling when one is set (`maxCostOverRetailPct`, percent over retail). The
 * VERDICT never changes: `routeMargin` still says `under`, so the Models tab
 * keeps showing the loss; only the BLOCKER does (`routeBlockers` skips
 * `margin` when `grantFundedAllows`). A maker's own cash route is never
 * affected: only a provider in `GRANT_CAPABLE_PROVIDERS` (Bedrock) can be
 * named at all, `normalizeGrantFunded` drops any other name (a maker named
 * by hand, a mistyped case), and the caller passes an allowance only for a
 * provider the setting names (`grantFundedAllowanceFor`, which checks the
 * same list again). Review, 2026-10-06: the registry path used to lift any
 * named provider, makers included, while the static chain only ever asked
 * for Bedrock, so a value naming `anthropic` split the two paths.
 *
 * WHICH LEGS THE CEILING JUDGES. The legs the route PRICES ITSELF: input,
 * output, the long-context tier legs, and a cache leg for which the route
 * states its own rate. A cached token the route bills at its INPUT rate (no
 * cache rate of its own, the four open-weight rows above) is not a price of
 * its own: it is the input leg, already judged against the retail input. The
 * ceiling guards against a row typed wrong (the plan's 150 "admits R1 at
 * +130%, refuses a row typed wrong by 10x"); counting the structural cache
 * gap made those four rows 257% to 614% "over", so any ceiling the plan
 * recommends darkened them on the static chain where they serve today
 * (review, 2026-10-06), against the owner's "route everything via bedrock
 * first". The MARGIN verdict still counts that cache leg (it is a real
 * loss, and the tab shows it); only the ceiling leaves it out. So a ceiling
 * never darkens those four rows, and clearing `providers` is the one way
 * to stop them. The setting arrives through
 * `RouteBlockerContext.grantFunded`, read by the caller (lib/llm/routing/
 * grant-funded.ts), so this module stays pure. The SAME predicate gates the
 * static chain (lib/llm/routing/static-chain.ts), which is every environment
 * today: with the setting cleared, an above-retail Bedrock route goes dark on
 * both paths and the maker serves.
 *
 * Until 2026-10-06 this module said nothing about the OFF path, and the OFF
 * path had no margin gate at all; it now calls `marginAgainstRetail`,
 * `overRetailPctAgainstRetail` and `grantFundedAllows` for its Bedrock step.
 */
import { pricingWithCacheAtInput, retailModelPricing, type ModelPricing } from './catalog';
import { isConfirmedPricing, isWellFormedPricing, routeCost, type CacheBilling, type ProviderRow, type RegistryRow, type RouteRow, type SkipReason } from './registry-types';

/** One leg's verdict: holds, loses money, cannot be judged, or does not apply to this route. */
export type MarginVerdict = 'ok' | 'under' | 'unknown' | 'n/a';

export interface RouteMargin {
  input: MarginVerdict;
  output: MarginVerdict;
  cacheRead: MarginVerdict;
  cacheWrite: MarginVerdict;
  longContext: MarginVerdict;
}

const UNKNOWN: RouteMargin = { input: 'unknown', output: 'unknown', cacheRead: 'unknown', cacheWrite: 'unknown', longContext: 'unknown' };

/** Strictly under, or both exactly zero (a leg nobody pays for loses nothing). */
function holds(routeLeg: number, retailLeg: number): MarginVerdict {
  if (!Number.isFinite(routeLeg) || !Number.isFinite(retailLeg)) return 'unknown';
  if (routeLeg === 0 && retailLeg === 0) return 'ok';
  return routeLeg < retailLeg ? 'ok' : 'under';
}

const num = (v: string | undefined): number | undefined => (v == null ? undefined : parseFloat(v));

type Legs = { inputPer1k: string; outputPer1k: string; cacheReadPer1k?: string; cacheWritePer1k?: string };

/**
 * A cache leg as `computeProxyCostNanocredits` bills it: the stated rate,
 * else the input rate. The same reading for the route's cost and for the
 * retail, because `retailForModel` has already removed the retail's cache
 * legs under `input` billing.
 */
function cacheLeg(legs: Legs, which: 'cacheReadPer1k' | 'cacheWritePer1k'): number {
  return num(legs[which]) ?? num(legs.inputPer1k)!;
}

function compareLegs(route: Legs, retail: Legs, cacheApplies: boolean): Omit<RouteMargin, 'longContext'> {
  return {
    input: holds(num(route.inputPer1k)!, num(retail.inputPer1k)!),
    output: holds(num(route.outputPer1k)!, num(retail.outputPer1k)!),
    cacheRead: cacheApplies ? holds(cacheLeg(route, 'cacheReadPer1k'), cacheLeg(retail, 'cacheReadPer1k')) : 'n/a',
    cacheWrite: cacheApplies ? holds(cacheLeg(route, 'cacheWritePer1k'), cacheLeg(retail, 'cacheWritePer1k')) : 'n/a',
  };
}

/** The worst of several verdicts: any `under` loses, then any `unknown`; `ok` and `n/a` hold. */
function worst(verdicts: MarginVerdict[]): MarginVerdict {
  return verdicts.includes('under') ? 'under' : verdicts.includes('unknown') ? 'unknown' : 'ok';
}

const tierLegs = (p: ModelPricing): Legs => ({
  inputPer1k: p.longContext!.inputPer1k,
  outputPer1k: p.longContext!.outputPer1k,
  // A tier that does not restate a cache rate keeps the flat one, as
  // `resolveEffectivePricing` bills it.
  cacheReadPer1k: p.longContext!.cacheReadPer1k ?? p.cacheReadPer1k,
  cacheWritePer1k: p.longContext!.cacheWritePer1k ?? p.cacheWritePer1k,
});

/**
 * The retail the user is billed for a model row: its list price, without
 * the cache legs under `input` billing, times the retail multiplier. The
 * same arithmetic `resolveRetailModelPricing` runs on every charge path for
 * a row, so lane C's admin view and lane D's tab show the price a settle
 * will actually bill.
 */
export function retailForModel(listPrice: ModelPricing, cacheBilling: CacheBilling): ModelPricing {
  return retailModelPricing(cacheBilling === 'input' ? pricingWithCacheAtInput(listPrice) : listPrice);
}

/**
 * The per-leg margin of a route against a model's list price. `listPrice`
 * is the model's `list_price` (retail is computed here, never passed in);
 * `route` needs only its cost, its checked date and its probe flag.
 */
export function routeMargin(
  listPrice: ModelPricing | null | undefined,
  cacheBilling: CacheBilling,
  route: Pick<RouteRow, 'cost' | 'costCheckedAt' | 'reportsCachedTokens'>,
): RouteMargin {
  const cost = routeCost(route);
  if (!cost || !isWellFormedPricing(listPrice)) return UNKNOWN;
  return marginAgainstRetail(retailForModel(listPrice, cacheBilling), cost, route.reportsCachedTokens);
}

/**
 * The per-leg margin of a cost against a retail the caller already holds
 * (the static chain has `resolveRetailModelPricing(model)`, the price every
 * charge path bills). `routeMargin` is this with the retail computed from a
 * model row. A malformed retail or cost is `unknown` everywhere.
 */
export function marginAgainstRetail(retail: ModelPricing | null | undefined, cost: ModelPricing | null | undefined, reportsCachedTokens: boolean | null = null): RouteMargin {
  if (!isWellFormedPricing(retail) || !isWellFormedPricing(cost)) return UNKNOWN;
  const cacheApplies = reportsCachedTokens !== false;
  const flat = compareLegs(cost, retail, cacheApplies);

  let longContext: MarginVerdict = 'n/a';
  if (cost.longContext) {
    // A tiered route against the retail's tier when it has one, else the flat
    // retail: the user pays the flat retail on every token then, so the
    // route's tier price must stay under it.
    const routeTier = tierLegs(cost);
    const verdicts = Object.values(compareLegs(routeTier, retail.longContext ? tierLegs(retail) : retail, cacheApplies));
    // Both tiered, the route's threshold LOWER than the retail's: between the
    // two thresholds the platform pays the route's tier while the user still
    // pays the flat retail, so the route's tier must also stay under the flat
    // retail. (A route threshold above the retail's is the safe direction:
    // the user pays the retail tier while the route still charges its flat.)
    if (retail.longContext && cost.longContext.thresholdTokens < retail.longContext.thresholdTokens) {
      verdicts.push(...Object.values(compareLegs(routeTier, retail, cacheApplies)));
    }
    longContext = worst(verdicts);
  }
  return { ...flat, longContext };
}

/** Whether a margin lets the route serve: no leg `under`, and the two legs every call has (`input`, `output`) known. */
export function marginHolds(m: RouteMargin): boolean {
  if (m.input !== 'ok' || m.output !== 'ok') return false;
  return m.cacheRead !== 'under' && m.cacheWrite !== 'under' && m.longContext !== 'under';
}

// ---------------------------------------------------------------------------
// The grant-funded exception (header, THE GRANT-FUNDED EXCEPTION)
// ---------------------------------------------------------------------------

/**
 * The platform setting `models.grantFunded` (lib/platform/settings.ts):
 * which SERVING providers have their bill paid by a provider credit grant,
 * and how far over retail a route on one may cost. `maxCostOverRetailPct`
 * is percent OVER retail on the worst leg the route prices itself (header,
 * WHICH LEGS THE CEILING JUDGES; 150 admits a leg at 2.5x retail); null is
 * no ceiling.
 */
export interface GrantFundedSetting {
  providers: readonly string[];
  maxCostOverRetailPct: number | null;
}

/**
 * The coded default, the owner's word of 2026-10-06 (Bedrock first even
 * above retail, no ceiling). The same literal as `DEFAULTS['models.grantFunded']`
 * in lib/platform/settings.ts, which this isomorphic module cannot import;
 * tests/unit/llm/routing/static-chain-grant.test.ts pins the two equal.
 * Callers that do not read the database (unit tests, scripts) use it.
 */
export const GRANT_FUNDED_DEFAULT: Readonly<GrantFundedSetting> = Object.freeze({ providers: Object.freeze(['bedrock']), maxCostOverRetailPct: null });

/** The setting switched off: no provider is grant-funded, so the margin rule holds everywhere. */
export const GRANT_FUNDED_OFF: Readonly<GrantFundedSetting> = Object.freeze({ providers: Object.freeze([] as string[]), maxCostOverRetailPct: null });

/**
 * The SERVING providers a credit grant can pay for: suppliers that resell
 * the makers' models, never a maker's own API (whose bill is cash). Bedrock,
 * paid by AWS's grant (owner, 2026-10-06). A provider joins this list in
 * code, with its ADR, never by a setting: a name outside it is dropped by
 * `normalizeGrantFunded` and refused by `grantFundedAllowanceFor`.
 */
export const GRANT_CAPABLE_PROVIDERS: readonly string[] = Object.freeze(['bedrock']);

const grantName = (p: string): string => p.trim().toLowerCase();

/**
 * A stored value as the routing reads it. Admins write this key as JSON in
 * the Settings tab with no shape check on the write, so the read is the
 * check, and it FAILS CLOSED: anything that is not `{ providers: string[],
 * maxCostOverRetailPct?: number >= 0 | null }` reads as off (no provider is
 * grant-funded, the margin rule holds), which is the direction that can only
 * cost the platform a cheaper supplier, never money. `undefined` (a reader
 * that answered nothing) is the coded default, as `getSetting` would answer
 * for a key with no row. Names are trimmed and lower-cased (`Bedrock` names
 * Bedrock, as an admin means it), and a name outside
 * `GRANT_CAPABLE_PROVIDERS` is dropped (`grantFundedDroppedNames` lists
 * them, so the reader can log them once).
 */
export function normalizeGrantFunded(raw: unknown): GrantFundedSetting {
  if (raw === undefined) return { providers: [...GRANT_FUNDED_DEFAULT.providers], maxCostOverRetailPct: GRANT_FUNDED_DEFAULT.maxCostOverRetailPct };
  if (!isGrantFundedShape(raw)) return { providers: [], maxCostOverRetailPct: null };
  const providers = [...new Set(raw.providers.map(grantName).filter((p) => GRANT_CAPABLE_PROVIDERS.includes(p)))];
  return { providers, maxCostOverRetailPct: typeof raw.maxCostOverRetailPct === 'number' ? raw.maxCostOverRetailPct : null };
}

/** The names a well-shaped stored value lists that `normalizeGrantFunded` drops (not grant-capable), as written. Empty for any other value. */
export function grantFundedDroppedNames(raw: unknown): string[] {
  if (!isGrantFundedShape(raw)) return [];
  return raw.providers.filter((p) => grantName(p).length > 0 && !GRANT_CAPABLE_PROVIDERS.includes(grantName(p)));
}

/** Whether a stored value has the setting's shape (`normalizeGrantFunded` reads anything else as off). */
export function isGrantFundedShape(raw: unknown): raw is { providers: string[]; maxCostOverRetailPct?: number | null } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.providers) || !r.providers.every((p) => typeof p === 'string')) return false;
  const ceiling = r.maxCostOverRetailPct;
  return ceiling === undefined || ceiling === null || (typeof ceiling === 'number' && Number.isFinite(ceiling) && ceiling >= 0);
}

/** What a route on one grant-funded provider may cost: the setting's ceiling. Absent: the provider is not grant-funded. */
export interface GrantFundedAllowance {
  maxCostOverRetailPct: number | null;
}

/**
 * The allowance for one SERVING provider, or undefined when the setting does
 * not name it or it is not grant-capable (every maker's cash route, even
 * when a hand-built setting names it).
 */
export function grantFundedAllowanceFor(setting: GrantFundedSetting | null | undefined, provider: string): GrantFundedAllowance | undefined {
  if (!setting || !GRANT_CAPABLE_PROVIDERS.includes(provider) || !setting.providers.includes(provider)) return undefined;
  return { maxCostOverRetailPct: setting.maxCostOverRetailPct };
}

/** One leg's cost over the retail as percent over (0 = at retail, negative = under); null when not a finite pair. */
function overPct(routeLeg: number, retailLeg: number): number | null {
  if (!Number.isFinite(routeLeg) || !Number.isFinite(retailLeg)) return null;
  if (routeLeg === 0 && retailLeg === 0) return -100;
  if (retailLeg === 0) return Number.POSITIVE_INFINITY;
  return (routeLeg / retailLeg - 1) * 100;
}

/**
 * The legs the ceiling judges (header, WHICH LEGS THE CEILING JUDGES): input
 * and output always, and a cache leg only when the route states its own
 * rate for it. A cache leg billed at the route's input rate is the input
 * leg again, already judged against the retail input.
 */
function legOverPcts(route: Legs, retail: Legs, cacheApplies: boolean): Array<number | null> {
  const out = [overPct(num(route.inputPer1k)!, num(retail.inputPer1k)!), overPct(num(route.outputPer1k)!, num(retail.outputPer1k)!)];
  if (cacheApplies) {
    for (const which of ['cacheReadPer1k', 'cacheWritePer1k'] as const) {
      if (route[which] !== undefined) out.push(overPct(cacheLeg(route, which), cacheLeg(retail, which)));
    }
  }
  return out;
}

/**
 * The worst leg of a cost over a retail the caller holds, as percent over
 * retail, with the tier pairings `marginAgainstRetail` judges (a cache leg
 * the probe ruled out does not count; a tier is compared with the retail
 * tier, or the flat retail, and with the flat retail as well when its
 * threshold is lower), on the legs the route prices itself: a cache leg with
 * no rate of its own is left out (`legOverPcts`). Null when either price is
 * malformed. A route under retail on every such leg answers a negative
 * number, although its margin may still fail on a cache leg.
 */
export function overRetailPctAgainstRetail(retail: ModelPricing | null | undefined, cost: ModelPricing | null | undefined, reportsCachedTokens: boolean | null = null): number | null {
  if (!isWellFormedPricing(retail) || !isWellFormedPricing(cost)) return null;
  const cacheApplies = reportsCachedTokens !== false;
  const pcts = legOverPcts(cost, retail, cacheApplies);
  if (cost.longContext) {
    const routeTier = tierLegs(cost);
    pcts.push(...legOverPcts(routeTier, retail.longContext ? tierLegs(retail) : retail, cacheApplies));
    if (retail.longContext && cost.longContext.thresholdTokens < retail.longContext.thresholdTokens) {
      pcts.push(...legOverPcts(routeTier, retail, cacheApplies));
    }
  }
  if (pcts.some((p) => p === null)) return null;
  return Math.max(...(pcts as number[]));
}

/** `overRetailPctAgainstRetail` for a registry route against its model's list price, or null when either is unconfirmed. */
export function routeOverRetailPct(
  listPrice: ModelPricing | null | undefined,
  cacheBilling: CacheBilling,
  route: Pick<RouteRow, 'cost' | 'costCheckedAt' | 'reportsCachedTokens'>,
): number | null {
  const cost = routeCost(route);
  if (!cost || !isWellFormedPricing(listPrice)) return null;
  return overRetailPctAgainstRetail(retailForModel(listPrice, cacheBilling), cost, route.reportsCachedTokens);
}

/**
 * Whether the grant lets a route serve whose margin does not hold. Never
 * without an allowance (the provider is not grant-funded), never with input
 * or output `unknown` (fail closed on price), never with an unknown worst
 * leg, and never above the ceiling when one is set. Says nothing about a
 * margin that holds: callers ask only when `marginHolds` is false.
 */
export function grantFundedAllows(margin: RouteMargin, overRetailPctValue: number | null, grant: GrantFundedAllowance | null | undefined): boolean {
  if (!grant) return false;
  if (margin.input === 'unknown' || margin.output === 'unknown') return false;
  if (overRetailPctValue === null || Number.isNaN(overRetailPctValue)) return false;
  if (grant.maxCostOverRetailPct !== null && overRetailPctValue > grant.maxCostOverRetailPct) return false;
  return true;
}

/**
 * Whether a registry route serves ONLY because of the grant: a confirmed
 * cost and list price, a margin that does not hold, and the grant allowing
 * it. The resolver marks such a route for the settle's above-retail counter,
 * and the admin view shows it as "serving above retail on the provider's
 * credit grant", so the two cannot disagree.
 */
export function servesOnGrant(
  model: Pick<RegistryRow, 'listPrice' | 'listPriceCheckedAt' | 'cacheBilling'>,
  route: Pick<RouteRow, 'cost' | 'costCheckedAt' | 'reportsCachedTokens'>,
  grant: GrantFundedAllowance | null | undefined,
): boolean {
  if (!grant || !routeCost(route)) return false;
  const listPrice = isConfirmedPricing(model.listPrice, model.listPriceCheckedAt) ? model.listPrice : null;
  const m = routeMargin(listPrice, model.cacheBilling, route);
  if (marginHolds(m)) return false;
  return grantFundedAllows(m, routeOverRetailPct(listPrice, model.cacheBilling, route), grant);
}

/** The live facts only the caller knows, passed in so this module stays pure. Every field is optional: absent means "not a blocker". */
export interface RouteBlockerContext {
  /** The env brake (plan section 4): false when `BEDROCK_ROUTE` excludes this route's maker or is `none`. */
  envAllows?: boolean;
  /** Whether the pod can get a credential for this route's provider right now. */
  hasCredentials?: boolean;
  /** Whether the (model, provider) pair is cooling down (plan section 5.4). */
  coolingDown?: boolean;
  /** Whether this route's adapter refused the request's tool shape (counted `tools`, decided at build time). */
  toolsRefused?: boolean;
  /**
   * The grant allowance for THIS route's serving provider
   * (`grantFundedAllowanceFor(models.grantFunded, route.provider)`), or
   * absent when the provider is not grant-funded. Present: a margin that
   * does not hold is not a blocker when `grantFundedAllows` says so.
   */
  grantFunded?: GrantFundedAllowance;
}

/**
 * Why a route would not serve, in the resolver's order (plan section 5.3),
 * or an empty list when it may. Pure: the model row supplies the list price
 * and the cache billing, the provider row its routing switch (a missing
 * provider row counts as routing enabled, as a missing row is never a
 * silent off), the route its flag and cost, and `ctx` the live facts. The
 * model's own STATUS is not a route matter: `pending` and `retired` models
 * are stopped by `modelDisposition` before any chain is resolved.
 */
export function routeBlockers(
  model: Pick<RegistryRow, 'listPrice' | 'listPriceCheckedAt' | 'cacheBilling'>,
  provider: Pick<ProviderRow, 'routingEnabled'> | null | undefined,
  route: Pick<RouteRow, 'enabled' | 'cost' | 'costCheckedAt' | 'reportsCachedTokens'>,
  ctx: RouteBlockerContext = {},
): SkipReason[] {
  const out: SkipReason[] = [];
  if (!route.enabled) out.push('disabled');
  if (provider && provider.routingEnabled === false) out.push('provider_routing_off');
  if (ctx.envAllows === false) out.push('env');
  const cost = routeCost(route);
  if (!cost) {
    out.push('cost_unconfirmed');
  } else {
    const listPrice = isConfirmedPricing(model.listPrice, model.listPriceCheckedAt) ? model.listPrice : null;
    const m = routeMargin(listPrice, model.cacheBilling, route);
    // The verdict stands; the grant only lifts the blocker (header).
    if (!marginHolds(m) && !grantFundedAllows(m, routeOverRetailPct(listPrice, model.cacheBilling, route), ctx.grantFunded)) out.push('margin');
  }
  if (ctx.hasCredentials === false) out.push('no_credentials');
  if (ctx.coolingDown === true) out.push('cooldown');
  if (ctx.toolsRefused === true) out.push('tools');
  return out;
}

/** Ninety days, the staleness badge threshold (decision 17.6). */
export const STALE_COST_MS = 90 * 24 * 60 * 60 * 1000;

/** Whether a cost was last checked more than 90 days before `now`. A badge in the tab and a digest line, never a serving gate. */
export function isStaleCost(checkedAt: Date | string | null | undefined, now: Date = new Date()): boolean {
  if (checkedAt == null) return false;
  const at = checkedAt instanceof Date ? checkedAt.getTime() : new Date(checkedAt).getTime();
  if (!Number.isFinite(at)) return false;
  return now.getTime() - at > STALE_COST_MS;
}
